import { CommonModule } from '@angular/common';
import { Component } from '@angular/core';
import { MatExpansionModule } from '@angular/material/expansion';

export type ComparisonPlanId = 'society' | 'growth' | 'operator';
export type ComparisonCell = boolean | string;

export interface ComparisonRow {
  name: string;
  values: Record<ComparisonPlanId, ComparisonCell>;
}

export interface ComparisonCategory {
  name: string;
  rows: ComparisonRow[];
}

const growthPlus: Record<ComparisonPlanId, ComparisonCell> = { society: false, growth: true, operator: true };
const operatorOnly: Record<ComparisonPlanId, ComparisonCell> = { society: false, growth: false, operator: true };

@Component({
  selector: 'app-pricing-comparison',
  standalone: true,
  imports: [CommonModule, MatExpansionModule],
  templateUrl: './pricing-comparison.component.html',
  styleUrl: './pricing-comparison.component.scss'
})
export class PricingComparisonComponent {
  readonly plans: { id: ComparisonPlanId; label: string }[] = [
    { id: 'society', label: 'Society' },
    { id: 'growth', label: 'Growth' },
    { id: 'operator', label: 'Operator' }
  ];

  readonly coreIncluded = [
    'Membership records, levels, and renewals',
    'Automated dues renewal reminder emails',
    'Events, registration, and attendee management',
    'Member communications and email',
    'Online payments and dues',
    'Directories and custom fields',
    'Standard reports and dashboards',
    'Hosted member portal with custom domain and branding',
    'Included Support: email, chat, and phone, plus knowledge base and onboarding docs'
  ];

  readonly categories: ComparisonCategory[] = [
    {
      name: 'At a glance',
      rows: [
        {
          name: 'Who it is for',
          values: {
            society: 'Most organizations',
            growth: 'Complex organizations',
            operator: 'Multi-org operators'
          }
        },
        {
          name: 'Starting price',
          values: {
            society: '$299/mo',
            growth: '$599/mo',
            operator: 'From $1,500/mo'
          }
        },
        {
          name: 'Included active members',
          values: {
            society: '500',
            growth: '1,500',
            operator: '1,500 across up to 3 orgs'
          }
        },
        {
          name: 'Each extra active member',
          values: {
            society: '$1/mo, billed monthly',
            growth: '$1/mo, billed monthly',
            operator: 'Custom terms'
          }
        }
      ]
    },
    {
      name: 'Advanced operations',
      rows: [
        { name: 'Advanced permissions', values: growthPlus },
        { name: 'Custom workflow (automation) builder', values: growthPlus },
        { name: 'Advanced reporting', values: growthPlus },
        { name: 'Dedicated onboarding specialist', values: growthPlus }
      ]
    },
    {
      name: 'Integrations',
      rows: [
        { name: 'API access', values: growthPlus },
        { name: 'Accounting exports', values: growthPlus },
        { name: 'Custom integrations', values: operatorOnly }
      ]
    },
    {
      name: 'Support',
      rows: [
        { name: 'Higher-priority product support', values: growthPlus },
        { name: 'Managed Support', values: { society: 'From $499/mo', growth: 'From $499/mo', operator: 'From $499/mo' } }
      ]
    },
    {
      name: 'Multi-organization operations',
      rows: [
        { name: 'Operator Workspace', values: operatorOnly },
        { name: 'Portfolio dashboard', values: operatorOnly },
        { name: 'Shared staff access', values: operatorOnly },
        { name: 'Separate branding per organization', values: operatorOnly },
        { name: 'Cross-organization alerts', values: operatorOnly },
        { name: 'Portfolio reporting', values: operatorOnly }
      ]
    }
  ];

  cellLabel(value: ComparisonCell): string {
    if (value === true) {
      return 'Included';
    }
    if (value === false) {
      return 'Not included';
    }
    return value;
  }

  isIncluded(value: ComparisonCell): boolean {
    return value === true;
  }

  isExcluded(value: ComparisonCell): boolean {
    return value === false;
  }

  isNote(value: ComparisonCell): boolean {
    return !this.isIncluded(value) && !this.isExcluded(value);
  }
}
